import React, { useEffect, useState } from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    horizontalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Componente para cada imagem/vídeo
function SortableItem({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        width: "100px",
        height: "100px",
        borderRadius: "6px",
        overflow: "hidden",
        border: "1px solid #ddd",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        cursor: "grab"
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            {children}
        </div>
    );
}

// Componente para cada bloco (sortable)
function SortableBlock({ bloco, children }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: bloco.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        border: "1px solid #ccc",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "16px",
        background: "#fafafa",
        width: "100%"
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <h4 {...listeners}>{bloco.titulo}</h4>
            {children}
        </div>
    );
}

// Componente principal
export default function KanbanDndKit({ blocosVitrine, onChange, navigateToVitrine }) {
    const [blocos, setBlocos] = useState([]);

    // Agrupa blocos por SKU e monta estado interno
    useEffect(() => {
        if (!blocosVitrine) return;

        const blocosPorSku = {};
        blocosVitrine.forEach((item) => {
            if (!blocosPorSku[item.sku]) {
                blocosPorSku[item.sku] = {
                    id: `bloco-${item.sku}`,
                    titulo: `${item.nomeprod} (${item.sku})`,
                    imagens: [],
                    sku: item.sku
                };
            }
            blocosPorSku[item.sku].imagens.push({
                id: `img-${item.sku}-${blocosPorSku[item.sku].imagens.length + 1}`,
                url: item.url
            });
        });

        const blocosArray = Object.values(blocosPorSku);
        setBlocos(blocosArray);
        onChange && onChange(blocosArray); // envia para o parent
    }, [blocosVitrine, onChange]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    // Drag-and-drop
    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (!over) return;

        // Drag de blocos
        const oldBlocoIndex = blocos.findIndex((b) => b.id === active.id);
        const newBlocoIndex = blocos.findIndex((b) => b.id === over.id);
        if (oldBlocoIndex !== -1 && newBlocoIndex !== -1 && oldBlocoIndex !== newBlocoIndex) {
            const newBlocos = arrayMove(blocos, oldBlocoIndex, newBlocoIndex);
            setBlocos(newBlocos);
            onChange && onChange(newBlocos);
            return;
        }

        // Drag de imagens
        blocos.forEach((bloco, blocoIndex) => {
            const imgIndex = bloco.imagens.findIndex((img) => img.id === active.id);
            if (imgIndex === -1) return;

            const overBloco = blocos.find((b) =>
                b.imagens.some((img) => img.id === over.id)
            );
            if (!overBloco || overBloco.id !== bloco.id) return; // só permite dentro do mesmo bloco

            const newIndex = bloco.imagens.findIndex((img) => img.id === over.id);
            const newImagens = arrayMove(bloco.imagens, imgIndex, newIndex);

            const newBlocos = [...blocos];
            newBlocos[blocoIndex] = { ...bloco, imagens: newImagens };
            setBlocos(newBlocos);
            onChange && onChange(newBlocos);
        });
    };

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={blocos.map(b => b.id)} strategy={verticalListSortingStrategy}>
                {blocos.map((bloco) => (
                    <SortableBlock
                        key={bloco.id}
                        bloco={bloco}
                        style={{ display: "flex", flexDirection: "column", padding: "8px", border: "1px solid #ccc", borderRadius: "8px" }}
                    >
                        <SortableContext items={bloco.imagens.map(i => i.id)} strategy={horizontalListSortingStrategy}>
                            <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                                {bloco.imagens.map((img) => (
                                    <SortableItem key={img.id} id={img.id}>
                                        {img.url.endsWith(".mp4") ? (
                                            <video src={img.url} width="100%" height="100%" controls />
                                        ) : (
                                            <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                        )}
                                    </SortableItem>
                                ))}
                            </div>
                        </SortableContext>

                        {/* botão sempre no final */}
                        <div style={{ marginTop: "auto", textAlign: "end", paddingTop: "8px" }}>
                            <button
                                onClick={() => navigateToVitrine(bloco.sku)}
                                style={{
                                    backgroundColor: "#4CAF50", // verde agradável
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "6px",
                                    padding: "8px 16px",
                                    cursor: "pointer",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                    fontWeight: "bold",
                                    transition: "all 0.2s ease",
                                }}
                                onMouseEnter={e => e.currentTarget.style.backgroundColor = "#45a049"}
                                onMouseLeave={e => e.currentTarget.style.backgroundColor = "#4CAF50"}
                            >
                                Mais Mídias
                            </button>
                        </div>
                    </SortableBlock>
                ))}
            </SortableContext>
        </DndContext>



    );
}
